/**
 * Analytics Service
 * Tracks user activities and app usage statistics
 */
import usageSync from './usageSync';

class AnalyticsService {
  constructor() {
    this.storageKey = 'sleekHearAnalytics';
    this.sessionKey = 'sleekHearSession';
    this.initializeSession();
  }

  initializeSession() {
    // Generate unique session ID
    const sessionId = this.generateSessionId();
    const timestamp = new Date().toISOString();

    sessionStorage.setItem(this.sessionKey, JSON.stringify({
      sessionId,
      startTime: timestamp,
      isActive: true
    }));

    // Track app launch
    this.trackEvent('app_launched', {
      timestamp,
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      screenResolution: `${window.screen.width}x${window.screen.height}`
    });
  }

  generateSessionId() {
    return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  getAnalyticsData() {
    const data = localStorage.getItem(this.storageKey);
    return data ? JSON.parse(data) : this.initializeAnalyticsData();
  }

  initializeAnalyticsData() {
    const initialData = {
      totalSessions: 0,
      totalEvents: 0,
      sessions: [],
      events: [],
      statistics: {
        bibleSearches: 0,
        songSearches: 0,
        songsDisplayed: 0,
        versesDisplayed: 0,
        voiceSearchUsed: 0,
        aiSongSearchUsed: 0,
        displayWindowOpened: 0,
        settingsChanged: 0
      },
      userActivity: {
        daily: {},
        weekly: {},
        monthly: {}
      },
      popularVerses: {},
      popularSongs: {},
      featureUsage: {}
    };
    this.saveAnalyticsData(initialData);
    return initialData;
  }

  saveAnalyticsData(data) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(data));
    } catch (error) {
      console.error('Failed to save analytics:', error);
    }
  }

  trackEvent(eventName, eventData = {}) {
    try {
      const analytics = this.getAnalyticsData();
      const session = JSON.parse(sessionStorage.getItem(this.sessionKey) || '{}');

      const event = {
        id: this.generateEventId(),
        name: eventName,
        timestamp: new Date().toISOString(),
        sessionId: session.sessionId,
        data: eventData
      };

      // Add event to events array
      analytics.events.push(event);
      analytics.totalEvents++;

      // Keep only last 1000 events to prevent storage overflow
      if (analytics.events.length > 1000) {
        analytics.events = analytics.events.slice(-1000);
      }

      // Update statistics based on event type
      this.updateStatistics(analytics, eventName, eventData);

      // Update time-based activity
      this.updateActivityTracking(analytics, event.timestamp);

      this.saveAnalyticsData(analytics);

      // Also send it to the central log the Admin Dashboard reads (queued, offline-safe).
      usageSync.enqueue('event', eventName, eventData, session.sessionId);
    } catch (error) {
      console.error('Failed to track event:', error);
    }
  }

  generateEventId() {
    return 'event_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  updateStatistics(analytics, eventName, eventData) {
    const stats = analytics.statistics;

    switch (eventName) {
      case 'app_launched':
        analytics.totalSessions++;
        // Add session to sessions array
        analytics.sessions.push({
          sessionId: eventData.sessionId || this.generateSessionId(),
          startTime: eventData.timestamp || new Date().toISOString(),
          userAgent: eventData.userAgent,
          platform: eventData.platform
        });
        // Keep only last 500 sessions
        if (analytics.sessions.length > 500) {
          analytics.sessions = analytics.sessions.slice(-500);
        }
        break;
      case 'bible_search':
        stats.bibleSearches++;
        break;
      case 'song_search':
        stats.songSearches++;
        break;
      case 'song_displayed':
        stats.songsDisplayed++;
        if (eventData.title) {
          analytics.popularSongs[eventData.title] =
            (analytics.popularSongs[eventData.title] || 0) + 1;
        }
        break;
      case 'verse_displayed':
        stats.versesDisplayed++;
        if (eventData.reference) {
          analytics.popularVerses[eventData.reference] =
            (analytics.popularVerses[eventData.reference] || 0) + 1;
        }
        break;
      case 'voice_search_used':
        stats.voiceSearchUsed++;
        break;
      case 'ai_song_search_used':
        stats.aiSongSearchUsed++;
        break;
      case 'display_window_opened':
        stats.displayWindowOpened++;
        break;
      case 'settings_changed':
        stats.settingsChanged++;
        break;
      default:
        break;
    }

    // Track feature usage
    analytics.featureUsage[eventName] = (analytics.featureUsage[eventName] || 0) + 1;
  }

  updateActivityTracking(analytics, timestamp) {
    const date = new Date(timestamp);
    const dateKey = date.toISOString().split('T')[0]; // YYYY-MM-DD
    const weekKey = this.getWeekKey(date);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

    // Daily activity
    if (!analytics.userActivity.daily[dateKey]) {
      analytics.userActivity.daily[dateKey] = { count: 0, events: [] };
    }
    analytics.userActivity.daily[dateKey].count++;

    // Weekly activity
    if (!analytics.userActivity.weekly[weekKey]) {
      analytics.userActivity.weekly[weekKey] = { count: 0 };
    }
    analytics.userActivity.weekly[weekKey].count++;

    // Monthly activity
    if (!analytics.userActivity.monthly[monthKey]) {
      analytics.userActivity.monthly[monthKey] = { count: 0 };
    }
    analytics.userActivity.monthly[monthKey].count++;

    // Keep only last 90 days of daily data
    const dailyKeys = Object.keys(analytics.userActivity.daily).sort();
    if (dailyKeys.length > 90) {
      const keysToRemove = dailyKeys.slice(0, dailyKeys.length - 90);
      keysToRemove.forEach(key => delete analytics.userActivity.daily[key]);
    }
  }

  getWeekKey(date) {
    const yearStart = new Date(date.getFullYear(), 0, 1);
    const weekNumber = Math.ceil(((date - yearStart) / 86400000 + yearStart.getDay() + 1) / 7);
    return `${date.getFullYear()}-W${String(weekNumber).padStart(2, '0')}`;
  }

  getStatistics() {
    const analytics = this.getAnalyticsData();

    // Calculate additional metrics
    const recentEvents = analytics.events.slice(-100); // Last 100 events
    const avgEventsPerSession = analytics.totalSessions > 0
      ? (analytics.totalEvents / analytics.totalSessions).toFixed(2)
      : 0;

    return {
      overview: {
        totalSessions: analytics.totalSessions,
        totalEvents: analytics.totalEvents,
        avgEventsPerSession
      },
      statistics: analytics.statistics,
      popularVerses: this.getTopItems(analytics.popularVerses, 10),
      popularSongs: this.getTopItems(analytics.popularSongs, 10),
      featureUsage: analytics.featureUsage,
      recentActivity: {
        daily: analytics.userActivity.daily,
        weekly: analytics.userActivity.weekly,
        monthly: analytics.userActivity.monthly
      },
      recentEvents: recentEvents.reverse(),
      sessions: analytics.sessions.slice(-50).reverse() // Last 50 sessions
    };
  }

  getTopItems(items, limit = 10) {
    return Object.entries(items)
      .sort((a, b) => b[1] - a[1])
      .slice(0, limit)
      .map(([name, count]) => ({ name, count }));
  }

  clearAnalytics() {
    localStorage.removeItem(this.storageKey);
    return this.initializeAnalyticsData();
  }

  exportAnalytics() {
    const analytics = this.getAnalyticsData();
    const blob = new Blob([JSON.stringify(analytics, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sleekHear-analytics-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

// Create singleton instance
const analyticsService = new AnalyticsService();

export default analyticsService;
